import type { Label } from '@doneverse/shared'
import { slugify, toTaskLists, UNCATEGORISED_LIST_KEY } from '@doneverse/shared'
import type { TaskScope } from '@/store'

/**
 * URL search params for the tasks board. These are filters, not preferences:
 * they belong to the view the user is looking at, so they live in the URL like
 * every other filtered list in this app (`/admin/users`,
 * `/groups/$groupId`). That makes a filtered board linkable, restores it
 * on browser back, and survives the page unmounting into a task or the create
 * form — none of which component state or a global store gives you.
 *
 * Absent means default: no `q` is no search, no `internal` is "show internal".
 * Only non-defaults are written, so a clean board has a clean URL.
 *
 * The selected **project** is deliberately NOT here — it's the entity the board
 * is about, so it's a path segment (`/tasks/project/$projectSlug/kanban`), the
 * same as every other entity in `router.tsx`. Search params carry view state
 * only. A collection *is* a search param, because it narrows a board rather
 * than being what the board is about, and a task can be in several at once.
 *
 * `taskScope` ("My tasks only") is also not here — it's a sticky user
 * preference that outlives any one board, so it stays persisted in the store.
 *
 * "Hide completed" *is* here, and defaults to on: it narrows what this board
 * shows, so a link to a board has to carry it or it opens showing something
 * else.
 */
/**
 * The board's four views.
 *
 * Which one is showing is a property of the *route*, not of the URL string:
 * every tab route declares it in `staticData` and the page reads it off the
 * matched route (`router.tsx`). Parsing it out of a pathname is what this
 * replaced — the page then had to answer "which tab?" for paths that are not
 * the board at all (`/tasks/<uuid>`, mid-transition), and answered "loading".
 */
export type TasksTab = 'list' | 'kanban' | 'calendar' | 'collections'

export type TasksSearch = {
  q?: string
  internal?: boolean
  /**
   * How the List tab sections its rows. Absent means by status, which is what
   * the board has always done and stays the default.
   */
  group?: 'project'
  /** Narrow every view to one collection (a `kind = 'collection'` label). */
  collection?: string
  /**
   * Put finished work back on the board.
   *
   * Absent is the default and the default is *hidden* — a board is a list of
   * what is left to do, and on a workspace a few months old the completed pile
   * is most of it. Written the un-negated way round (`completed=true` shows
   * them) so the param says what the reader sees rather than what they don't.
   */
  completed?: boolean
}

/** Server-resolved filter set the board's task queries run with. */
export interface TasksBoardFilters {
  search?: string
  mine?: boolean
  /** Preview the founder view by omitting internal_only tasks (non-founders). */
  excludeInternal?: boolean
  /** Server-side narrowing to one collection. */
  collection_id?: string
  /**
   * Server-side narrowing to one project — a `kind = 'list'` label id, or
   * {@link UNCATEGORISED_LIST_KEY} for the tasks in no project at all.
   */
  list_id?: string
  /** Drop completed tasks ("Hide completed", on by default). */
  excludeCompleted?: boolean
}

/**
 * The same filters for a query that already names one `status`.
 *
 * A Kanban column and a list section are each a single status, so
 * `excludeCompleted` has nothing left to say there — and sent alongside
 * `status: 'completed'` it contradicts it, emptying the column instead of
 * hiding it. Those views drop the completed section entirely (see
 * {@link boardStatuses}) and query the rest through here.
 */
export function statusQueryFilters(filters: TasksBoardFilters): TasksBoardFilters {
  const { excludeCompleted: _excludeCompleted, ...rest } = filters
  return rest
}

/**
 * The status sections a per-status view renders, with completed dropped when
 * it is being hidden. Shared with the route loaders so the keys they warm are
 * the keys the board then reads.
 */
export function boardStatuses<T extends string>(statuses: T[], filters: TasksBoardFilters): T[] {
  return filters.excludeCompleted ? statuses.filter((status) => status !== 'completed') : statuses
}

/**
 * Route `validateSearch` for /tasks and its tab children.
 *
 * Keeps only params that are both well-formed and non-default, which is the
 * one rule this module has — see `cleanTasksSearch`, which is the same rule
 * pointed the other way.
 */
export function validateTasksSearch(search: Record<string, unknown>): TasksSearch {
  const result: TasksSearch = {}
  if (typeof search.q === 'string' && search.q) result.q = search.q
  if (search.internal === false) result.internal = false
  if (search.group === 'project') result.group = 'project'
  if (typeof search.collection === 'string' && search.collection) result.collection = search.collection
  if (search.completed === true) result.completed = true
  return result
}

/**
 * Drops params that are back at their default so they leave the URL.
 *
 * The same predicate as `validateTasksSearch` — "keep only what isn't the
 * default" — so it delegates rather than restating it. Written out twice, a new
 * param added to one half and forgotten in the other fails silently and in
 * opposite directions: validate-only leaves it stuck in the URL forever,
 * clean-only drops it out of a shared link.
 */
export function cleanTasksSearch(search: TasksSearch): TasksSearch {
  return validateTasksSearch(search)
}

// ─── Projects in the path ────────────────────────────────────────
//
// "Project" is the founder-facing word for a `kind = 'list'` label: exactly one
// per task, DB-enforced by `task_labels_one_list`. That cardinality is the whole
// reason it can be the board's *scope* — every task is in one project or none,
// so switching projects partitions the board instead of overlapping it.
//
// (A `kind = 'collection'` label is "Collection" in UI copy — many per task, so
// it can only ever filter. See docs/architecture.md.)

/**
 * The path segment for "the tasks that are in no project".
 *
 * A real slug rather than an absent segment, because "no project" is an
 * ordinary place to be — on day one it is every task — so it has to be
 * linkable in its own right, not just reachable by clearing the filter.
 */
export const NO_PROJECT_SLUG = 'no-project'

/**
 * A project's URL segment: its name, slugified.
 *
 * A name, not an id, because `labels_unique_name` already makes names unique
 * per workspace per kind (case-insensitively), and a raw uuid in the address
 * bar tells the founder nothing. Renaming a project therefore changes its URL,
 * which is what a slug means everywhere else in this app.
 *
 * Falls back to the id when the name has no slug-safe characters at all (a
 * project named only with emoji), so every project stays addressable.
 */
export function projectSlug(project: Label): string {
  return slugify(project.name) || project.id
}

/**
 * The project a slug refers to, or `undefined` for "all projects".
 *
 * Both the /tasks loader and the page resolve the slug through here, so the
 * filters they build are identical and the warmed cache actually hits.
 *
 * Order matters. {@link NO_PROJECT_SLUG} is checked first so a project a
 * founder happens to name "No project" cannot shadow the no-project bucket,
 * and the id is tried last so a link built before a rename still resolves.
 */
export function resolveProjectListId(projects: Label[], slug: string | undefined): string | undefined {
  if (!slug) return undefined
  if (slug === NO_PROJECT_SLUG) return UNCATEGORISED_LIST_KEY
  const taskProjects = toTaskLists(projects)
  const bySlug = taskProjects.find((project) => projectSlug(project) === slug)
  if (bySlug) return bySlug.id
  return taskProjects.find((project) => project.id === slug)?.id
}

/**
 * The single derivation of query filters from the URL + the scope preference.
 * The /tasks loader prefetches with these and the page renders with them; the
 * warmed cache only hits while the two agree, and a divergence is invisible
 * (the board just silently re-fetches). So both sides call this.
 *
 * `canSeeInternal` is false for founders — they never receive internal_only
 * tasks, so the toggle is meaningless for them.
 *
 * `projectListId` comes from the path, not from `search`, which is why it's a
 * separate argument rather than a field on `TasksSearch`.
 */
export function taskBoardFilters(
  search: TasksSearch,
  scope: TaskScope,
  canSeeInternal: boolean,
  projectListId?: string,
): TasksBoardFilters {
  return {
    search: search.q?.trim() || undefined,
    mine: scope === 'mine',
    excludeInternal: canSeeInternal && search.internal === false,
    collection_id: search.collection || undefined,
    list_id: projectListId || undefined,
    excludeCompleted: !search.completed,
  }
}
